/**
 * UserList - User management with invites
 *
 * Features:
 * - Table view with all users
 * - Invite modal for new users via email
 * - Role assignment
 * - Active/inactive toggle
 * - Delete user
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import {
  fetchUsers,
  fetchRoles,
  updateUser,
  resendInvite,
} from '../../../../api';
import Button from '../../../../shared/components/ui/Button';
import Modal from '../../../../shared/components/ui/Modal';
import Spinner from '../../../../shared/components/ui/Spinner';
import { Btn, Drawer } from '../../../../shared/components/redesign';
import styles from './UserList.module.css';

function SortHeader({ label, sortKeyName, sortKey, sortDir, onSort }) {
  return (
    <th
      className={clsx(styles.th, styles.sortable)}
      onClick={() => onSort(sortKeyName)}
    >
      {label}
      {sortKey === sortKeyName && (
        <span className={styles.sortIndicator}>{sortDir === 'asc' ? ' ^' : ' v'}</span>
      )}
    </th>
  );
}

const STATUS_FILTERS = [
  { key: 'all', label: 'All Users' },
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Pending Invite' },
  { key: 'inactive', label: 'Inactive' },
];

function UserDrawer({ user, roles, onSave, onClose }) {
  const [form, setForm] = useState({
    name: user?.name || '',
    custom_role_id: user?.custom_role_id || roles[0]?.id || '',
    is_active: !!user?.is_active,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      console.error('Failed to save user:', err);
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <>
      <div className={styles.userInfo}>
        <span className={styles.userEmail}>{user.email}</span>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Name</label>
        <input
          type="text"
          className={styles.input}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="User name"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label}>Role</label>
        <select
          className={styles.select}
          value={form.custom_role_id}
          onChange={(e) => setForm({ ...form, custom_role_id: e.target.value })}
        >
          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            className={styles.checkbox}
          />
          <span>Active</span>
        </label>
        <p className={styles.hint}>
          Inactive users cannot log in but their data is preserved.
        </p>
      </div>
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow="§ USER"
      title={user?.name || user?.email || 'User'}
      subtitle={user?.email && user?.name ? user.email : undefined}
      ariaLabel="Edit user"
      body={body}
      footer={(
        <>
          <Btn kind="ghost" size="sm" onClick={onClose}>Cancel</Btn>
          <span className={styles.footerSpacer} />
          <Btn kind="primary" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Btn>
        </>
      )}
    />
  );
}

export default function UserList() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');

  // Sorting
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  // Confirmation/alert modal state (replaces browser alert)
  const [successMessage, setSuccessMessage] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, rolesData] = await Promise.all([
        fetchUsers({ limit: 200 }),
        fetchRoles({ limit: 100 }),
      ]);
      setUsers(usersData.results || usersData || []);
      setRoles(rolesData.results || rolesData || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let result = users;

    // Search filter
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (u) =>
          u.name?.toLowerCase().includes(term) ||
          u.email?.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter === 'active') {
      result = result.filter((u) => u.is_active && !u.pending_invite);
    } else if (statusFilter === 'pending') {
      result = result.filter((u) => u.pending_invite);
    } else if (statusFilter === 'inactive') {
      result = result.filter((u) => !u.is_active);
    }

    // Role filter
    if (roleFilter !== 'all') {
      result = result.filter((u) => u.custom_role_id === roleFilter);
    }

    // Sort
    result = [...result].sort((a, b) => {
      const rawA = a[sortKey] || '';
      const rawB = b[sortKey] || '';
      const aVal = typeof rawA === 'string' ? rawA.toLowerCase() : rawA;
      const bVal = typeof rawB === 'string' ? rawB.toLowerCase() : rawB;

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [users, search, statusFilter, roleFilter, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleSaveUser = async (formData) => {
    await updateUser(editingUser.id, formData);
    loadData();
  };

  const handleResendInvite = async (user) => {
    try {
      await resendInvite(user.id);
      setSuccessMessage('Invitation resent successfully');
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find((r) => r.id === roleId);
    return role?.name || 'Unknown';
  };

  if (loading && users.length === 0) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Users</h2>
          <p className={styles.subtitle}>
            Manage team members and their access permissions
          </p>
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className={styles.filters}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          className={styles.filterSelect}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">All Roles</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        <span className={styles.count}>{filteredUsers.length} users</span>
      </div>

      {/* Table */}
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <SortHeader label="Name" sortKeyName="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Email" sortKeyName="email" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <SortHeader label="Role" sortKeyName="custom_role_id" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
              <th className={styles.th}>Status</th>
              <th className={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className={styles.row}>
                <td className={styles.td}>
                  <span className={styles.userName}>{user.name || '-'}</span>
                </td>
                <td className={styles.td}>
                  <span className={styles.userEmail}>{user.email}</span>
                </td>
                <td className={styles.td}>
                  <span className={styles.roleBadge}>{getRoleName(user.custom_role_id)}</span>
                </td>
                <td className={styles.td}>
                  {user.pending_invite ? (
                    <span className={clsx(styles.statusBadge, styles.pending)}>
                      Pending
                    </span>
                  ) : user.is_active ? (
                    <span className={clsx(styles.statusBadge, styles.active)}>
                      Active
                    </span>
                  ) : (
                    <span className={clsx(styles.statusBadge, styles.inactive)}>
                      Inactive
                    </span>
                  )}
                </td>
                <td className={styles.td}>
                  <div className={styles.actions}>
                    {user.pending_invite && (
                      <Btn
                        kind="ghost"
                        size="sm"
                        onClick={() => handleResendInvite(user)}
                      >
                        Resend
                      </Btn>
                    )}
                    <Btn kind="ghost" size="sm" onClick={() => handleEdit(user)}>
                      Edit
                    </Btn>
                  </div>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {showEditModal && editingUser && (
        <UserDrawer
          user={editingUser}
          roles={roles}
          onSave={handleSaveUser}
          onClose={() => {
            setShowEditModal(false);
            setEditingUser(null);
          }}
        />
      )}

      {/* Success Message Modal (replaces browser alert) */}
      <Modal
        isOpen={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        size="sm"
      >
        <Modal.Header onClose={() => setSuccessMessage(null)}>
          Success
        </Modal.Header>
        <Modal.Body>
          <p>{successMessage}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setSuccessMessage(null)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
